import type { ReactNode } from "react";

/**
 * Minimal markdown → ReactNode renderer.
 * Supports: # / ## / ### headings, paragraphs, **bold**, *italic*, `code`,
 * [text](url) links, - and 1. lists, blockquotes (>), and --- horizontal rules.
 *
 * Intentionally not a full parser — keeps the deps small. Upgrade to `marked`
 * or `markdown-it` if you need tables, footnotes, or task lists.
 */

type Block =
  | { kind: "h"; level: 2 | 3 | 4; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "blockquote"; text: string }
  | { kind: "hr" };

function parseBlocks(md: string): Block[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    if (trimmed === "---" || trimmed === "***") {
      blocks.push({ kind: "hr" });
      i += 1;
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(trimmed);
    if (heading) {
      const level = heading[1].length;
      const mapped = level === 1 ? 2 : level === 2 ? 3 : 4;
      blocks.push({ kind: "h", level: mapped as 2 | 3 | 4, text: heading[2] });
      i += 1;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i += 1;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }

    if (trimmed.startsWith("> ")) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("> ")) {
        buf.push(lines[i].trim().slice(2));
        i += 1;
      }
      blocks.push({ kind: "blockquote", text: buf.join(" ") });
      continue;
    }

    const buf: string[] = [trimmed];
    i += 1;
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|>\s|[-*]\s|\d+\.\s|---|\*\*\*)/.test(lines[i].trim())) {
      buf.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ kind: "p", text: buf.join(" ") });
  }

  return blocks;
}

/**
 * Allow only http(s), mailto, tel, anchor (#…), and relative paths.
 * Reject javascript:, data:, vbscript:, file:, and anything else.
 */
function sanitizeHref(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("#")) return trimmed;
  if (trimmed.startsWith("/") || trimmed.startsWith("./") || trimmed.startsWith("../")) {
    return trimmed;
  }
  const schemeM = /^([a-zA-Z][a-zA-Z0-9+.\-]*):/.exec(trimmed);
  if (!schemeM) return trimmed; // schemeless → treat as relative
  const scheme = schemeM[1].toLowerCase();
  if (scheme === "http" || scheme === "https" || scheme === "mailto" || scheme === "tel") {
    return trimmed;
  }
  return null;
}

function renderInline(text: string): ReactNode[] {
  // tokens we recognize, in order: code, link, bold, italic
  const out: ReactNode[] = [];
  const pattern =
    /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > lastIndex) {
      out.push(text.slice(lastIndex, m.index));
    }
    const tok = m[0];
    if (tok.startsWith("`")) {
      out.push(
        <code key={`md-${key++}`} className="rounded px-1.5 py-0.5 text-[0.95em]" style={{ backgroundColor: "rgba(8,45,61,0.06)" }}>
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith("**")) {
      out.push(
        <strong key={`md-${key++}`} style={{ fontWeight: 700 }}>
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith("[")) {
      const linkM = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (linkM) {
        const safeHref = sanitizeHref(linkM[2]);
        if (safeHref) {
          const external = /^https?:\/\//i.test(safeHref);
          out.push(
            <a
              key={`md-${key++}`}
              href={safeHref}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
              className="underline"
              style={{ textUnderlineOffset: "3px" }}
            >
              {linkM[1]}
            </a>,
          );
        } else {
          // unsafe scheme — render label as plain text, drop the link
          out.push(linkM[1]);
        }
      }
    } else if (tok.startsWith("*")) {
      out.push(
        <em key={`md-${key++}`} style={{ fontStyle: "italic" }}>
          {tok.slice(1, -1)}
        </em>,
      );
    }
    lastIndex = m.index + tok.length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

export function renderMarkdown(md: string): ReactNode[] {
  return parseBlocks(md).map((block, idx) => {
    const key = `b-${idx}`;
    if (block.kind === "hr") {
      return (
        <hr
          key={key}
          className="my-8 border-0 h-px w-24 mx-auto"
          style={{ backgroundColor: "rgba(8,45,61,0.18)" }}
        />
      );
    }
    if (block.kind === "h") {
      if (block.level === 2) return <h2 key={key}>{renderInline(block.text)}</h2>;
      if (block.level === 3) return <h3 key={key}>{renderInline(block.text)}</h3>;
      return <h4 key={key}>{renderInline(block.text)}</h4>;
    }
    if (block.kind === "ul") {
      return (
        <ul key={key}>
          {block.items.map((it, j) => (
            <li key={`${key}-${j}`}>{renderInline(it)}</li>
          ))}
        </ul>
      );
    }
    if (block.kind === "ol") {
      return (
        <ol key={key}>
          {block.items.map((it, j) => (
            <li key={`${key}-${j}`}>{renderInline(it)}</li>
          ))}
        </ol>
      );
    }
    if (block.kind === "blockquote") {
      return <blockquote key={key}>{renderInline(block.text)}</blockquote>;
    }
    return <p key={key}>{renderInline(block.text)}</p>;
  });
}
